/**
 * normalizePrototypeAction - 将 data-proto-click 的原始 action 字符串
 * 映射到 ACTION_REGISTRY 中的 AccessAction。
 *
 * 职责（spec §7.3 / §3.6 P1）：
 * - 解析 `showArticle(3)` -> `chapter.read`（带 resource.index=3）
 * - 解析 `submitIdea()` -> `community.idea.submit`
 * - 解析 `toggleVote(this, 'idea_123', 1)` -> `community.idea.vote`
 * - 解析 `toggleStar(this, 'chapter_123')` -> `activity.favorite.write`
 * - 解析 `submitAiTopic()` -> `ai.course.create`
 * - 解析 `triggerFeedbackFileUpload()` -> `upload.feedback`
 * - 解析 `runAiSearch()` -> `ai.history.read`（AI 历史搜索）
 * - 解析媒体控制 `setMode(this, 'audio'|'video')`、`togglePlay()` 等 -> `media.audio.play` / `media.video.play`
 * - 不匹配的 action 返回 null（让服务端或 action router 自行裁决，不视为错误）
 *
 * 调用方（PrototypeController onClick/onChange/onInput）拿到 AccessAction 后
 * 调 resolveClientAccess(state, { action, resource }) 做 L1 预判。
 *
 * 设计原则：
 * - **不重复 visitorGuard 的正则白名单**：白名单是"这个 action 对 visitor 是否安全"
 *   的判断，本函数只做"这个 action 属于哪个 AccessAction"的映射。
 *   visitor 可达性由 ACTION_REGISTRY.spec.requiresSession + capability 决定，
 *   accessResolver 内置判断。
 * - **parser 不抛异常**：任何无法解析的 action 都返回 null，调用方按 null 处理
 *   （退化为旧行为：无 L1 预判，直接走 action router，服务端 accessPolicyService
 *   仍是真相源）
 * - **parser 不做副作用**：纯函数，不读 DOM，不触发请求。resource.index 等信息
 *   从 action 字符串中解析。
 *
 * 安全边界：
 * - 不得从 DOM dataset 读取敏感字段（如 ownerId）；owner 校验由服务端 L4 做
 * - 不得自行推断 isOwner；accessResolver 收到 isOwner=null 时按"不确定"处理
 */

import type { AccessAction } from "../../domain/types/accessTypes";

export interface NormalizedPrototypeAction {
	/** AccessAction key；null 表示无对应映射 */
	action: AccessAction | null;
	/** 资源 hint（chapter index 等）；undefined 表示无资源信息 */
	resource?: {
		type?: "chapter" | "aiJob" | "privateCourse";
		index?: number;
		/** 客户端是否已知为 owner（保守 false，让服务端裁决） */
		isOwner?: boolean;
	};
}

/**
 * 解析 data-proto-click 原始字符串。
 *
 * @param rawAction 原始 action 字符串（如 "showArticle(3)"）
 * @param element 触发元素（用于推断资源类型，如 chapter card）
 */
export function normalizePrototypeAction(
	rawAction: string,
	element?: HTMLElement | null,
): NormalizedPrototypeAction {
	const a = rawAction ?? "";
	if (!a) return { action: null };

	// ===== 章节阅读 =====
	// History 的续学按钮从自身 dataset 读取已验证的章节索引。不能复用
	// showArticle(N)：后者会按“首次打开”写入 0% 进度，破坏已有续学进度。
	if (/\bresumeHistoryChapter\b/.test(a)) {
		const index = Number(element?.dataset.historyChapterIndex);
		return {
			action: "chapter.read",
			resource: { type: "chapter", index: Number.isInteger(index) && index > 0 ? index : undefined },
		};
	}

	// showArticle(N) - 打开第 N 章
	const articleMatch = a.match(/\bshowArticle\(\s*(\d+)/);
	if (articleMatch) {
		const index = Number(articleMatch[1]);
		// 若点击的卡片标记为付费/私人课程，需要用对应 action，但客户端无法确定
		// 课程来源 -> 保守返回 chapter.read，服务端 accessPolicyService 会判断
		// 这是 public 还是 private 资源并返回对应决策
		return {
			action: "chapter.read",
			resource: { type: "chapter", index },
		};
	}

	// chapter.unlock - 点击"展开完整正文"按钮（后续 75% 正文解锁）
	if (/\b(?:retryArticleBody|unlockChapter|expandBody)\b/.test(a)) {
		const index = inferChapterIndex(element);
		return {
			action: "chapter.unlock",
			resource: { type: "chapter", index },
		};
	}

	// ===== 媒体访问 =====
	const topicPlayMatch = a.match(/\bplayTopicChapter\(\s*(\d+)/);
	if (topicPlayMatch) {
		return { action: 'media.audio.play', resource: { type: 'chapter', index: Number(topicPlayMatch[1]) } };
	}
	// setMode(this, 'audio' | 'video') - 切换播放模式（注意 audio/video 由参数决定）
	const setAudioMatch = a.match(/\bsetMode\([^,]+,\s*['"]audio['"]/);
	if (setAudioMatch) {
		return { action: "media.audio.play", resource: { type: "chapter", index: inferChapterIndex(element) } };
	}
	const setVideoMatch = a.match(/\bsetMode\([^,]+,\s*['"]video['"]/);
	if (setVideoMatch) {
		return { action: "media.video.play", resource: { type: "chapter", index: inferChapterIndex(element) } };
	}
	// playArticleAudio() / togglePlay() / showPlayer() - 当前模式由 runtime.audioMode 决定，但 parser 无状态
	// 取 conservatively：默认 audio。triggerVideoPaywall / switchVideoToAudio 是 video。
	// 客户端预判只做 capability 级别（免费 vs 付费），具体 audio/video 不影响决策。
	if (/\b(?:playArticleAudio|togglePlay|showPlayer|cycleAudioSpeed|seekAudioRelative|switchAudioToVideo)\b/.test(a)) {
		return { action: "media.audio.play", resource: { type: "chapter", index: inferChapterIndex(element) } };
	}
	if (/\b(?:toggleVideoPlay|cycleVideoSpeed|seekVideoRelative|switchVideoToAudio|toggleVideoPip|toggleVideoFullscreen|closeVideoFullscreen|stopVideoFullscreen|triggerVideoPaywall|resumeTopicVideo|shareVfChapter)\b/.test(a)) {
		return { action: "media.video.play", resource: { type: "chapter", index: inferChapterIndex(element) } };
	}

	// ===== Activity（收藏 / 进度 / 投票） =====
	if (/\b(?:toggleStar|addFavorite|removeFavorite)\b/.test(a)) {
		return { action: "activity.favorite.write" };
	}
	if (/\b(?:markProgress|setProgress|updateProgress)\b/.test(a)) {
		return { action: "activity.progress.write" };
	}
	if (/\btoggleVote\b/.test(a)) {
		return { action: "activity.topicVote.write" };
	}

	// ===== Community（读 / 投票 / 提案 / 评论） =====
	// 读：showView('community')、showSocial、filterCommunity、runCommunitySearch
	if (/\b(?:showView\(\s*['"]community['"]|setActiveTab\(\s*['"]community['"]|showSocial\(|setSocialSubTab\(|filterCommunity\(|runCommunitySearch\(|clearCommunitySearch\(|showIdeaDetail\(|openCommunityIdeaDialog\(|showCommunityIdeaTopic\(|backToCommunity\()/.test(a)) {
		return { action: "community.idea.read" };
	}
	if (/\b(?:showSubmitIdea|submitIdea|wizardNext\(|addIdeaLink)\b/.test(a)) {
		return { action: "community.idea.submit" };
	}
	if (/\b(?:toggleIdeaVote|voteIdea|voteDown)\b/.test(a)) {
		return { action: "community.idea.vote" };
	}
	if (/\b(?:addIdeaComment|submitComment)\b/.test(a)) {
		return { action: "community.comment.submit" };
	}

	// ===== GitHub Trending =====
	// 读：filterGhTrending / runGhSearch / clearGhSearch / showView('github-trending')
	if (/\b(?:showView\(\s*['"]github-trending['"]|setActiveTab\(\s*['"]github-trending['"]|filterGhTrending\(|runGhSearch\(|clearGhSearch\()/.test(a)) {
		return { action: "github.summary.read" };
	}
	// Legacy card actions are still routed to their public Topic detail.
	if (/\bopenGhRepo\b/.test(a)) {
		return { action: 'topic.read' };
	}

	// ===== AI Create / 历史 =====
	// AI Create 营销落地页公开；历史、任务与创建能力仍分别鉴权。
	if (/\b(?:showView\(\s*['"]ai-create['"]|setActiveTab\(\s*['"]ai-create['"]|openAiCreateFromMember\()/.test(a)) {
		return { action: 'ai.landing.read' };
	}
	// 历史读：showView('history') / filterAiCreate / runAiSearch / clearAiSearch
	if (/\b(?:showView\(\s*['"]history['"]|filterAiCreate\(|runAiSearch\(|clearAiSearch\()/.test(a)) {
		return { action: "ai.history.read" };
	}
	if (/\b(?:showAiJobDetail|openAiSubmissionDialog|retryAiSubmissionDialog)\b/.test(a)) {
		return { action: 'ai.job.read', resource: { type: 'aiJob' } };
	}
	// Source uploads are non-replayable: after a login redirect browsers discard
	// the selected local file, so they must never be recorded as course-create
	// intents. Keep this before the broader AI wizard matcher below.
	if (/\b(?:addAiAttachment|handleAiAttachment)\b/.test(a)) {
		return { action: "upload.ai" };
	}
	if (/\b(?:addIdeaAttachment|handleIdeaAttachment)\b/.test(a)) {
		return { action: "upload.community" };
	}

	// 创建 AI 课程
	if (/\b(?:submitAiTopic|aiWizardNext\(|addAiLink)\b/.test(a)) {
		return { action: "ai.course.create" };
	}
	// 打开创建入口本身仍是公开转化动作。launchAiCreate 会根据登录态与
	// 实时额度决定展示付费墙、耗尽提示或创建向导；真正提交继续走
	// ai.course.create，服务端权限与配额校验不变。
	if (/\bshowAiCreateModal\b/.test(a)) {
		return { action: 'ai.landing.read' };
	}

	// ===== 私人课程 =====
	// privateCourse.read - 客户端无法确定 isOwner，留空让服务端裁决
	// 但 triggerOpenPrivateCourse 标识意图
	if (/\b(?:openPrivateCourse|showPrivateCourseDetail)\b/.test(a)) {
		return {
			action: "privateCourse.read",
			resource: { type: "privateCourse", isOwner: false },
		};
	}

	// ===== 账户 / 账单 =====
	if (/\b(?:showView\(\s*['"]profile['"]|setActiveTab\(\s*['"]profile['"])/.test(a)) {
		return { action: 'profile.dashboard.read' };
	}
	if (/\b(?:showView\(\s*['"]feedback['"]|setActiveTab\(\s*['"]feedback['"]|openFeedbackFromUpgradeResume)/.test(a)) {
		return { action: 'feedback.view' };
	}
	if (/\b(?:cancelAutoRenew|confirmCancelAutoRenew|resumeAutoRenew|resumeUpgradeRenew|closeUpgradeResumeModal|openSubscriptionPanel|confirmUpgradeFromPrompt|closeUpgradeConfirmModal)/.test(a)) {
		return { action: "billing.manage" };
	}
	if (/\b(?:saveProfile|updateProfile|updateAvatar|updateDisplayName)\b/.test(a)) {
		return { action: "account.manage" };
	}

	// ===== 上传 =====
	if (/\b(?:triggerFeedbackFileUpload|clearFeedbackFile)\b/.test(a)) {
		return { action: "upload.feedback" };
	}

	// ===== Topic 目录（读） =====
	// showView('home-browse') / filterBrowse / runBrowseSearch - 都是 topic.read
	if (/\b(?:showView\(\s*['"]home-browse['"]|setActiveTab\(\s*['"]home-browse['"]|filterBrowse\(|setBrowseSort\(|runBrowseSearch\(|clearBrowseSearch\(|navigateToBrowse\(|showTopicDetail\(|openHistoryTopic\b)/.test(a)) {
		return { action: "topic.read" };
	}

	// ===== 未匹配：返回 null，退化为旧行为 =====
	return { action: null };
}

/**
 * 从元素上下文推断当前章节 index。
 *
 * 当前没有 chapter index 的 dataset attribute，所以返回 undefined。
 * 如果未来给 chapter card 加 data-chapter-index，这里可解析。
 */
function inferChapterIndex(element?: HTMLElement | null): number | undefined {
	if (!element) return undefined;
	const card = element.closest<HTMLElement>("[data-chapter-index]");
	if (card) {
		const idx = Number(card.dataset.chapterIndex);
		if (Number.isFinite(idx)) return idx;
	}
	return undefined;
}
